
import React, { useMemo } from "react";
import { NursingHome } from "@/hooks/use-nursing-home";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

interface ProfileCompletionCardProps {
  nursingHome: NursingHome;
}

export function ProfileCompletionCard({ nursingHome }: ProfileCompletionCardProps) {
  const completionPercentage = useMemo(() => {
    // Required fields that should always be completed
    const requiredFields = [
      'center_name', 
      'description', 
      'standardized_phone_number',
      'email',
      'street_name',
      'town',
      'county',
    ];
    
    // Optional fields that add to completion percentage
    const optionalFields = [
      'official_website',
      'hiqa_profile_url',
      'person_in_charge',
      'manager_name',
      'price_range',
      'facilities',
      'services',
      'amenities',
      'room_types',
      'year_established',
      'max_residents',
      'latitude',
      'longitude',
      'visiting_hours',
      'category',
    ];
    
    // Count required fields
    const requiredCompleted = requiredFields.filter(field => 
      nursingHome[field as keyof NursingHome] !== null && 
      nursingHome[field as keyof NursingHome] !== undefined && 
      nursingHome[field as keyof NursingHome] !== ''
    ).length;
    
    // Count optional fields
    const optionalCompleted = optionalFields.filter(field => {
      const value = nursingHome[field as keyof NursingHome];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== undefined && value !== '';
    }).length;
    
    // Calculate percentage based on weighted values
    const totalWeight = requiredFields.length + optionalFields.length;
    const completedWeight = requiredCompleted + optionalCompleted;
    
    return Math.round((completedWeight / totalWeight) * 100);
  }, [nursingHome]);

  return (
    <Card className="p-4 w-full md:w-auto">
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <p className="text-sm font-medium">Profile Completion</p>
          <span className="text-sm font-bold">{completionPercentage}%</span>
        </div>
        <Progress value={completionPercentage} className="h-2" />
      </div>
    </Card>
  );
}
